import { useLocaleState, useRecordContext } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";

import { StatusBadge } from "../faromarket-shared/StatusBadge";
import { formatRelativeDate } from "../misc/RelativeDate";

const STATUS_CHOICES = [
  { id: "pending", name: "crm.faromarket.automation_status.pending" },
  { id: "running", name: "crm.faromarket.automation_status.running" },
  { id: "success", name: "crm.faromarket.automation_status.success" },
  { id: "failed", name: "crm.faromarket.automation_status.failed" },
  { id: "skipped", name: "crm.faromarket.automation_status.skipped" },
  {
    id: "partial_success",
    name: "crm.faromarket.automation_status.partial_success",
  },
];

const RunFilters = [
  <SearchInput source="automation_key@ilike" alwaysOn />,
  <SelectInput source="status" choices={STATUS_CHOICES} translateChoice />,
];

const StatusField = () => {
  const record = useRecordContext();
  return <StatusBadge value={record?.status} kind="automation_status" />;
};

const RelativeDateField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const [locale = "pt-BR"] = useLocaleState();
  const value = record?.[source];
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span>{formatRelativeDate(value, locale)}</span>;
};

const ErrorField = () => {
  const record = useRecordContext();
  if (!record?.error_message)
    return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className="text-xs text-red-700 dark:text-red-300 truncate max-w-[40ch] inline-block"
      title={record.error_message}
    >
      {record.error_message}
    </span>
  );
};

export const AutomationRunList = () => (
  <List
    perPage={25}
    sort={{ field: "started_at", order: "DESC" }}
    filter={{ is_test: false }}
    filters={RunFilters}
  >
    <DataTable>
      <DataTable.Col
        source="automation_key"
        label="resources.automation_runs.fields.automation_key"
      />
      <DataTable.Col
        source="status"
        label="resources.automation_runs.fields.status"
      >
        <StatusField />
      </DataTable.Col>
      <DataTable.Col
        source="trigger_type"
        label="resources.automation_runs.fields.trigger_type"
      />
      <DataTable.Col
        source="started_at"
        label="resources.automation_runs.fields.started_at"
      >
        <RelativeDateField source="started_at" />
      </DataTable.Col>
      <DataTable.Col
        source="finished_at"
        label="resources.automation_runs.fields.finished_at"
      >
        <RelativeDateField source="finished_at" />
      </DataTable.Col>
      <DataTable.Col
        source="duration_ms"
        label="resources.automation_runs.fields.duration_ms"
      />
      <DataTable.Col
        source="form_submission_id"
        label="resources.automation_runs.fields.form_submission_id"
      >
        <ReferenceField
          source="form_submission_id"
          reference="form_submissions"
          link={false}
          empty="—"
        />
      </DataTable.Col>
      <DataTable.Col
        source="lead_id"
        label="resources.automation_runs.fields.lead_id"
      >
        <ReferenceField
          source="lead_id"
          reference="leads"
          link={false}
          empty="—"
        />
      </DataTable.Col>
      <DataTable.Col
        source="error_message"
        label="resources.automation_runs.fields.error_message"
      >
        <ErrorField />
      </DataTable.Col>
    </DataTable>
  </List>
);
